import { useId } from 'react';
import styles from './ThemeBanner.module.css';

export type ThemeBannerKind =
  | 'hero'
  | 'mobile-betting'
  | 'cash-out'
  | 'live-casino'
  | 'casino-games'
  | 'results';

type ThemeBannerProps = {
  kind: ThemeBannerKind;
  className?: string;
};

/**
 * Vector stand-ins for the tottalo promo photography (slider banners and the
 * results teaser). Those JPEGs bake in tottalo's blue/green palette and its
 * blue display type, so they clash under every other theme. These draw the
 * same subjects from theme variables instead, so one set covers all palettes.
 */
export function ThemeBanner({ kind, className }: ThemeBannerProps) {
  if (kind === 'results') {
    return <ResultsBanner className={className} />;
  }
  return <SliderBanner kind={kind} className={className} />;
}

/** The accent ramp both banners paint their headline and key artwork with — a
 *  flat accent reads as a coloured block, a ramp reads as metal. */
function AccentRamp({ id }: { id: string }) {
  return (
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="var(--theme-banner-ramp-1)" />
        <stop offset="55%" stopColor="var(--theme-banner-ramp-2)" />
        <stop offset="100%" stopColor="var(--theme-banner-ramp-3)" />
      </linearGradient>
      {/* The focal light has to fall off to nothing, or its edge reads as a
          drawn ellipse rather than as light. */}
      <radialGradient id={`${id}-glow`}>
        <stop
          offset="0%"
          stopColor="var(--theme-banner-ramp-2)"
          stopOpacity="0.22"
        />
        <stop
          offset="100%"
          stopColor="var(--theme-banner-ramp-2)"
          stopOpacity="0"
        />
      </radialGradient>
    </defs>
  );
}

const SLIDER_WIDTH = 546;
const SLIDER_HEIGHT = 210;

type SliderCopy = {
  light: string;
  bold: string;
  lightSize?: number;
  boldSize?: number;
};

const SLIDER_COPY: Record<Exclude<ThemeBannerKind, 'results'>, SliderCopy> = {
  hero: {
    light: 'BEST ODDS. BIG WINS.',
    bold: 'BET & WIN',
    lightSize: 17,
    boldSize: 48,
  },
  'mobile-betting': { light: 'MOBILE', bold: 'BETTING' },
  'cash-out': { light: 'CASH', bold: 'OUT' },
  'live-casino': { light: 'LIVE', bold: 'CASINO' },
  'casino-games': { light: 'CASINO', bold: 'GAMES' },
};

/**
 * Floodlight rays, a pool of light and a scatter of sparks — the stadium
 * atmosphere the tottalo photography carried, rebuilt so it takes the palette.
 * Deterministic positions: a random scatter would re-roll on every render.
 */
const SPARKS = [
  [372, 42, 2.4],
  [418, 26, 1.5],
  [470, 58, 2],
  [352, 96, 1.6],
  [500, 108, 2.6],
  [400, 150, 1.8],
  [468, 168, 1.4],
  [520, 74, 1.6],
  [338, 140, 1.2],
] as const;

function StadiumBackdrop({ glowId }: { glowId: string }) {
  return (
    <>
      <ellipse cx="430" cy="105" rx="210" ry="145" fill={`url(#${glowId})`} />
      {/* Beams fanning down from an off-canvas rig above the right shoulder. */}
      <g className={styles.beam}>
        <path d="M470 -40 L604 250 L556 250 Z" />
        <path d="M470 -40 L520 250 L484 250 Z" />
        <path d="M470 -40 L392 250 L348 250 Z" />
      </g>
      <path className={styles.wash} d="M0 210 L200 0 L320 0 L120 210 Z" />
      <g className={styles.spark}>
        {SPARKS.map(([cx, cy, r]) => (
          <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={r} />
        ))}
      </g>
    </>
  );
}

function SliderBanner({
  kind,
  className,
}: {
  kind: Exclude<ThemeBannerKind, 'results'>;
  className?: string;
}) {
  const copy = SLIDER_COPY[kind];
  const gradientId = `${useId()}-ramp`;

  return (
    <svg
      className={[styles.banner, className].filter(Boolean).join(' ')}
      viewBox={`0 0 ${SLIDER_WIDTH} ${SLIDER_HEIGHT}`}
      role="img"
      aria-label={`${copy.light} ${copy.bold}`}
      preserveAspectRatio="xMidYMid slice"
    >
      <AccentRamp id={gradientId} />
      <rect className={styles.bg} width={SLIDER_WIDTH} height={SLIDER_HEIGHT} />
      <StadiumBackdrop glowId={`${gradientId}-glow`} />

      <text
        className={styles.titleLight}
        x="44"
        y="86"
        fontSize={copy.lightSize ?? 28}
      >
        {copy.light}
      </text>
      <text
        className={styles.titleBold}
        x="40"
        y="146"
        fontSize={copy.boldSize ?? 54}
        fill={`url(#${gradientId})`}
      >
        {copy.bold}
      </text>

      <g transform={ART_ORIGIN[kind]}>{SLIDER_ART[kind](gradientId)}</g>
    </svg>
  );
}

/* Artwork sits in a 170x170 box; each piece mirrors what the tottalo photo
   showed. Each is a function of the ramp id so a piece can paint itself with the
   palette's metallic ramp rather than a flat accent. */
type SliderArt = (rampId: string) => JSX.Element;

/* The hero ball is bigger than the line-art pieces, so it starts further right
   to keep clear of the headline. */
const ART_ORIGIN: Record<Exclude<ThemeBannerKind, 'results'>, string> = {
  hero: 'translate(368, 22)',
  'mobile-betting': 'translate(346, 20)',
  'cash-out': 'translate(346, 20)',
  'live-casino': 'translate(346, 20)',
  'casino-games': 'translate(346, 20)',
};

const SLIDER_ART: Record<Exclude<ThemeBannerKind, 'results'>, SliderArt> = {
  /* The match ball, lit from the upper left: ramp-filled sphere, knocked-out
     panels, a rim light and a specular. */
  hero: (rampId) => (
    <>
      {[
        { x: 0, y: 116, w: 150, o: 0.16 },
        { x: 18, y: 94, w: 118, o: 0.24 },
        { x: 30, y: 138, w: 92, o: 0.12 },
      ].map((streak) => (
        <rect
          key={streak.y}
          className={styles.artFill}
          x={streak.x}
          y={streak.y}
          width={streak.w}
          height="4"
          rx="2"
          opacity={streak.o}
        />
      ))}

      <circle cx="110" cy="84" r="62" fill={`url(#${rampId})`} />
      <circle
        className={styles.artStrokeThin}
        cx="110"
        cy="84"
        r="62"
        strokeWidth="2"
      />

      {/* Panels: knocked out of the sphere so they read as seams, not paint. */}
      <g className={styles.ballSeam}>
        <path d="M110 52 L131 68 L123 93 H97 L89 68 Z" />
        {[
          'M110 52 L110 24',
          'M131 68 L157 60',
          'M123 93 L140 116',
          'M97 93 L80 116',
          'M89 68 L63 60',
        ].map((d) => (
          <path key={d} d={d} />
        ))}
      </g>

      <ellipse
        cx="88"
        cy="58"
        rx="22"
        ry="15"
        fill="#fff"
        opacity="0.22"
        transform="rotate(-28 88 58)"
      />
      <ellipse
        className={styles.artFillMuted}
        cx="110"
        cy="156"
        rx="58"
        ry="8"
        opacity="0.18"
      />
    </>
  ),

  /* Phone beside a tablet, both showing a grid of betting tiles. */
  'mobile-betting': () => (
    <>
      <rect
        className={styles.artStroke}
        x="6"
        y="18"
        width="58"
        height="112"
        rx="9"
      />
      {[0, 1, 2, 3].map((row) =>
        [0, 1].map((col) => (
          <rect
            key={`p${row}${col}`}
            className={styles.artFillMuted}
            x={14 + col * 22}
            y={30 + row * 22}
            width="18"
            height="18"
            rx="3"
          />
        ))
      )}
      <rect
        className={styles.artStroke}
        x="78"
        y="36"
        width="118"
        height="80"
        rx="9"
      />
      {[0, 1, 2].map((row) =>
        [0, 1, 2, 3].map((col) => (
          <rect
            key={`t${row}${col}`}
            className={styles.artFillMuted}
            x={87 + col * 26}
            y={45 + row * 22}
            width="22"
            height="18"
            rx="3"
          />
        ))
      )}
    </>
  ),

  /* Falling coins. */
  'cash-out': () => (
    <>
      {[
        { cx: 46, cy: 42, r: 30 },
        { cx: 122, cy: 30, r: 22 },
        { cx: 152, cy: 96, r: 26 },
        { cx: 68, cy: 118, r: 20 },
        { cx: 12, cy: 96, r: 14 },
      ].map((coin, i) => (
        <g key={i}>
          <circle
            className={styles.artFill}
            cx={coin.cx}
            cy={coin.cy}
            r={coin.r}
            opacity="0.22"
          />
          <circle
            className={styles.artStroke}
            cx={coin.cx}
            cy={coin.cy}
            r={coin.r}
          />
          <text
            className={styles.coinGlyph}
            x={coin.cx}
            y={coin.cy + coin.r * 0.36}
            fontSize={coin.r * 1.05}
            textAnchor="middle"
          >
            $
          </text>
        </g>
      ))}
    </>
  ),

  /* Two dealt cards next to a roulette wheel. */
  'live-casino': () => (
    <>
      <g transform="rotate(-14 44 84)">
        <rect
          className={styles.artStroke}
          x="14"
          y="34"
          width="60"
          height="86"
          rx="8"
        />
        <path className={styles.artFill} d="M44 58 L57 76 L44 94 L31 76 Z" />
      </g>
      <g transform="rotate(10 92 88)">
        <rect
          className={styles.artStroke}
          x="62"
          y="40"
          width="60"
          height="86"
          rx="8"
        />
        <circle className={styles.artFill} cx="92" cy="83" r="13" />
      </g>
      <circle className={styles.artStroke} cx="152" cy="74" r="34" />
      <circle className={styles.artStrokeThin} cx="152" cy="74" r="20" />
      <circle className={styles.artFill} cx="152" cy="74" r="6" />
      {[0, 45, 90, 135].map((deg) => (
        <line
          key={deg}
          className={styles.artStrokeThin}
          x1={152 + 20 * Math.cos((deg * Math.PI) / 180)}
          y1={74 + 20 * Math.sin((deg * Math.PI) / 180)}
          x2={152 + 34 * Math.cos((deg * Math.PI) / 180)}
          y2={74 + 34 * Math.sin((deg * Math.PI) / 180)}
        />
      ))}
    </>
  ),

  /* Slot cabinet with three reels, plus a die. */
  'casino-games': () => (
    <>
      <rect
        className={styles.artStroke}
        x="14"
        y="16"
        width="112"
        height="132"
        rx="12"
      />
      <rect
        className={styles.artStrokeThin}
        x="28"
        y="34"
        width="84"
        height="52"
        rx="6"
      />
      {[0, 1, 2].map((i) => (
        <rect
          key={i}
          className={styles.artFillMuted}
          x={33 + i * 27}
          y="39"
          width="22"
          height="42"
          rx="4"
        />
      ))}
      <circle className={styles.artFill} cx="70" cy="112" r="12" />
      <path
        className={styles.artStroke}
        d="M126 60 h18 a10 10 0 0 1 0 20 h-4"
      />
      <rect
        className={styles.artStroke}
        x="122"
        y="100"
        width="46"
        height="46"
        rx="9"
        transform="rotate(12 145 123)"
      />
      {[
        [134, 112],
        [156, 118],
        [140, 134],
      ].map(([cx, cy], i) => (
        <circle key={i} className={styles.artFill} cx={cx} cy={cy} r="4.5" />
      ))}
    </>
  ),
};

/* The results teaser is a much smaller slot (230x87), so it gets its own layout. */
function ResultsBanner({ className }: { className?: string }) {
  const gradientId = `${useId()}-ramp`;

  return (
    <svg
      className={[styles.banner, className].filter(Boolean).join(' ')}
      viewBox="0 0 230 87"
      role="img"
      aria-label="Bet results"
      preserveAspectRatio="xMidYMid slice"
    >
      <AccentRamp id={gradientId} />
      <rect className={styles.bg} width="230" height="87" />
      <ellipse
        cx="184"
        cy="44"
        rx="86"
        ry="62"
        fill={`url(#${gradientId}-glow)`}
      />
      <path className={styles.wash} d="M0 87 L70 0 L124 0 L54 87 Z" />

      <text className={styles.titleLight} x="18" y="38" fontSize="13">
        BET
      </text>
      <text
        className={styles.titleBold}
        x="16"
        y="66"
        fontSize="22"
        fill={`url(#${gradientId})`}
      >
        RESULTS
      </text>

      {/* Rising bars with a trend arrow — the "results" idea without the grass. */}
      <g transform="translate(160, 18)">
        {[
          { x: 0, h: 18 },
          { x: 16, h: 30 },
          { x: 32, h: 44 },
        ].map((bar) => (
          <rect
            key={bar.x}
            className={styles.artFillMuted}
            x={bar.x}
            y={52 - bar.h}
            width="11"
            height={bar.h}
            rx="2"
          />
        ))}
        <path className={styles.artStroke} d="M2 30 L20 16 L34 22 L52 2" />
        <path
          className={styles.artFill}
          fill={`url(#${gradientId})`}
          d="M42 0 h12 v12 l-12 -12 Z"
        />
      </g>
    </svg>
  );
}
